import { useState, useEffect, useRef } from "react";
import { uploadFile, deleteUploadedFile } from "@/utils/apiClient";
import { siteData } from "@/store/siteData";
import type { AdItemServer as AdItem } from "@/store/siteData";
import { getAdSettings, saveAdSettings } from "@/components/feature/InterstitialAd";

export default function AdsManager() {
  const [ads, setAds] = useState<AdItem[]>([]);
  const [settings, setSettings] = useState(getAdSettings);
  const [durationInput, setDurationInput] = useState(String(getAdSettings().showDurationHours));
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadLink, setUploadLink] = useState("");
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadFile_, setUploadFile] = useState<File | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  // Sunucudan reklamları yükle
  const loadAds = () => {
    try {
      const stored = siteData.getAds();
      setAds(stored);
    } catch {
      setLoadError("Reklamlar yüklenirken hata oluştu.");
    }
  };

  useEffect(() => {
    loadAds();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    setUploadName(file.name.replace(/\.[^/.]+$/, ""));
    const url = URL.createObjectURL(file);
    setUploadPreview(url);
    setUploadError("");
  };

  const handleUpload = async () => {
    if (!uploadFile_) return;
    setUploading(true);
    setUploadError("");
    try {
      // Sunucuya yükle — tüm cihazlardan erişilebilir URL alıyoruz
      const { id: imageFileId, url: imageUrl } = await uploadFile(uploadFile_);

      const newAd: AdItem = {
        id: crypto.randomUUID(),
        imageUrl,
        imageFileId,
        link: uploadLink.trim(),
        active: false,
        name: uploadName.trim() || "Reklam",
        createdAt: Date.now(),
      };

      const updated = [...ads, newAd];
      await siteData.saveAds(updated);
      setAds(updated);

      // Temizle
      setUploadFile(null);
      if (uploadPreview) URL.revokeObjectURL(uploadPreview);
      setUploadPreview(null);
      setUploadName("");
      setUploadLink("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Yükleme başarısız.");
    } finally {
      setUploading(false);
    }
  };

  const handleToggleActive = async (id: string) => {
    const updated = ads.map(a => a.id === id ? { ...a, active: !a.active } : a);
    await siteData.saveAds(updated);
    setAds(updated);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const ad = ads.find(a => a.id === id);
      if (ad?.imageFileId) {
        try { await deleteUploadedFile(ad.imageFileId); } catch { /**/ }
      }
      const updated = ads.filter(a => a.id !== id);
      await siteData.saveAds(updated);
      setAds(updated);
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpdateLink = async (id: string, link: string) => {
    const updated = ads.map(a => a.id === id ? { ...a, link } : a);
    await siteData.saveAds(updated);
    setAds(updated);
  };

  const handleSaveSettings = async () => {
    const hours = parseFloat(durationInput);
    if (isNaN(hours) || hours < 0) return;
    const s = { showDurationHours: hours };
    await saveAdSettings(s);
    setSettings(s);
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2000);
  };

  const activeAd = ads.find(a => a.active);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight">Reklam Yönetimi</h1>
        <p className="text-[#7B8FAF] text-sm mt-1">Siteye ilk girişte gösterilecek reklam popup&apos;ını yönetin</p>
      </div>

      {/* Cross-platform notice */}
      <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-xl px-4 py-3 flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-global-line text-emerald-400 text-sm" />
        </div>
        <div>
          <p className="text-emerald-300 text-xs font-semibold">Platformlar Arası Senkronizasyon</p>
          <p className="text-emerald-400/70 text-xs mt-0.5">Reklamlar sunucuda saklanır. Hangi cihazdan yönetirseniz yönetin, tüm ziyaretçilere gösterilir.</p>
        </div>
      </div>

      {loadError && (
        <div className="bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-400 text-sm" />
          <p className="text-red-300 text-xs">{loadError}</p>
        </div>
      )}

      {/* How it works */}
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/15 flex-shrink-0">
            <i className="ri-information-line text-amber-400 text-xl" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm mb-1.5">Nasıl Çalışır?</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: "ri-image-add-line", title: "Reklam Yükle", desc: "PNG veya JPG görsel yükleyin, isteğe bağlı link ekleyin" },
                { icon: "ri-toggle-line", title: "Aktif Et", desc: "Aktif ettiğiniz reklam sitede ziyaretçilere gösterilir" },
                { icon: "ri-timer-line", title: "Süre Ayarla", desc: "Ziyaretçi reklamı gördükten sonra ne kadar bekleyeceğini belirleyin" },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#1E2B4A] flex-shrink-0">
                    <i className={`${s.icon} text-[#7B8FAF] text-sm`} />
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold">{s.title}</p>
                    <p className="text-[#7B8FAF] text-xs mt-0.5 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Settings */}
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-5">
        <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-settings-3-line text-[#7B8FAF]" />
          </div>
          Gösterim Ayarları
        </h2>
        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Yeniden Gösterim Süresi</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={0} step={0.5} value={durationInput}
                onChange={e => setDurationInput(e.target.value)}
                className="w-28 bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] transition-colors text-center"
              />
              <span className="text-[#7B8FAF] text-sm">saat</span>
              <span className="text-[#5E7398] text-xs">(0 = her ziyarette göster)</span>
            </div>
            <p className="text-[#5E7398] text-xs mt-1.5">
              Mevcut: <span className="text-[#A5B5CC]">{settings.showDurationHours} saat sonra tekrar gösterilir</span>
            </p>
          </div>
          <button
            onClick={handleSaveSettings}
            className={`px-5 py-2.5 text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center gap-2 ${
              settingsSaved ? "bg-emerald-600 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"
            }`}
          >
            {settingsSaved ? <><i className="ri-check-double-line" /> Kaydedildi!</> : <><i className="ri-save-line" /> Kaydet</>}
          </button>
        </div>
      </div>

      {/* Active ad status */}
      <div className={`rounded-xl p-4 border flex items-center gap-4 ${activeAd ? "bg-emerald-500/10 border-emerald-500/30" : "bg-[#141E35] border-[#2B3654]"}`}>
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${activeAd ? "bg-emerald-500/20" : "bg-white/5"}`}>
          <i className={`ri-advertisement-line text-xl ${activeAd ? "text-emerald-400" : "text-[#5E7398]"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${activeAd ? "text-emerald-300" : "text-[#7B8FAF]"}`}>
            {activeAd ? "Aktif Reklam Mevcut" : "Aktif Reklam Yok"}
          </p>
          <p className="text-[#5E7398] text-xs mt-0.5">
            {activeAd ? `"${activeAd.name}" — tüm platformlarda ziyaretçilere gösteriliyor` : "Aşağıdan bir reklam yükleyip aktif edin"}
          </p>
        </div>
        {activeAd?.imageUrl && (
          <div className="w-12 h-12 rounded-lg overflow-hidden border border-emerald-500/30 flex-shrink-0">
            <img src={activeAd.imageUrl} alt={activeAd.name} className="w-full h-full object-cover" />
          </div>
        )}
      </div>

      {/* Ads list */}
      {ads.length > 0 && (
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#2B3654] flex items-center justify-between">
            <h2 className="text-white font-semibold flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-image-2-line text-[#7B8FAF]" />
              </div>
              Yüklü Reklamlar
              <span className="text-[#5E7398] text-xs font-normal">({ads.length} adet)</span>
            </h2>
            <p className="text-[#5E7398] text-xs">Sadece 1 reklam aktif olabilir</p>
          </div>
          <div className="divide-y divide-[#1E2B4A]">
            {ads.map(ad => (
              <div key={ad.id} className="p-4 flex items-start gap-4">
                {/* Preview thumbnail */}
                <div
                  className="w-20 h-16 rounded-lg overflow-hidden border border-[#2B3654] flex-shrink-0 cursor-pointer relative group"
                  onClick={() => setPreviewId(previewId === ad.id ? null : ad.id)}
                >
                  {ad.imageUrl ? (
                    <img src={ad.imageUrl} alt={ad.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[#0F1729]">
                      <i className="ri-image-line text-[#5E7398] text-2xl" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-zoom-in-line text-white text-sm" />
                  </div>
                </div>

                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white text-sm font-medium truncate">{ad.name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${ad.active ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/25" : "text-[#5E7398] bg-white/5 border-white/10"}`}>
                      {ad.active ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-link text-[#5E7398] text-xs" />
                    </div>
                    <input
                      type="url"
                      defaultValue={ad.link}
                      onBlur={e => handleUpdateLink(ad.id, e.target.value)}
                      placeholder="https://... (isteğe bağlı link)"
                      className="flex-1 min-w-0 bg-[#0F1729] border border-[#2B3654] text-white text-xs rounded-lg px-2.5 py-1.5 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398]"
                    />
                  </div>
                  <p className="text-[#465886] text-xs">
                    {new Date(ad.createdAt).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" })}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleActive(ad.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all border ${
                      ad.active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30"
                        : "bg-white/5 text-[#7B8FAF] border-[#2B3654] hover:text-white hover:bg-white/10"
                    }`}
                  >
                    <i className={ad.active ? "ri-eye-fill" : "ri-eye-off-line"} />
                    {ad.active ? "Devre Dışı" : "Aktif Et"}
                  </button>
                  <button
                    onClick={() => handleDelete(ad.id)}
                    disabled={deletingId === ad.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all border text-red-400 bg-red-500/10 border-red-500/25 hover:bg-red-500/20 disabled:opacity-50"
                  >
                    {deletingId === ad.id
                      ? <><i className="ri-loader-4-line animate-spin" /> Siliniyor</>
                      : <><i className="ri-delete-bin-line" /> Sil</>
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full preview modal */}
      {previewId && ads.find(a => a.id === previewId)?.imageUrl && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewId(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPreviewId(null)}
              className="absolute -top-4 -right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white text-gray-900 cursor-pointer text-base font-bold"
            >
              ✕
            </button>
            <img
              src={ads.find(a => a.id === previewId)!.imageUrl}
              alt="Önizleme"
              className="max-w-full max-h-[85vh] object-contain rounded-xl"
            />
          </div>
        </div>
      )}

      {/* Upload new ad */}
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-5">
        <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-image-add-line text-[#7B8FAF]" />
          </div>
          Yeni Reklam Yükle
        </h2>

        <div className="space-y-4">
          {/* File upload area */}
          <div
            className="border-2 border-dashed border-[#2B3654] rounded-xl p-6 text-center cursor-pointer hover:border-[#3B82F6]/60 transition-colors relative"
            onClick={() => fileRef.current?.click()}
          >
            {uploadPreview ? (
              <div className="flex items-center gap-4 justify-center">
                <img src={uploadPreview} alt="Önizleme" className="h-24 object-contain rounded-lg" />
                <div className="text-left">
                  <p className="text-white text-sm font-medium">{uploadName || uploadFile_?.name}</p>
                  <p className="text-[#7B8FAF] text-xs mt-1">
                    {uploadFile_ ? `${(uploadFile_.size / 1024).toFixed(1)} KB` : ""}
                  </p>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      if (uploadPreview) URL.revokeObjectURL(uploadPreview);
                      setUploadFile(null);
                      setUploadPreview(null);
                      setUploadName("");
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                    className="text-red-400 text-xs mt-2 flex items-center gap-1 cursor-pointer hover:text-red-300 transition-colors"
                  >
                    <i className="ri-close-line" /> Kaldır
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0F1729] mx-auto mb-3">
                  <i className="ri-upload-cloud-2-line text-[#3B82F6] text-2xl" />
                </div>
                <p className="text-white text-sm font-medium">Görsel Seç veya Sürükle</p>
                <p className="text-[#5E7398] text-xs mt-1">PNG, JPG, GIF, WebP — Sunucuya yüklenir, her platformda görünür</p>
              </>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
          </div>

          {uploadFile_ && (
            <>
              <div>
                <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Reklam Adı</label>
                <input
                  type="text"
                  value={uploadName}
                  onChange={e => setUploadName(e.target.value)}
                  placeholder="Reklam açıklaması..."
                  className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398]"
                />
              </div>
              <div>
                <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">
                  Link <span className="text-[#465886] font-normal normal-case">(isteğe bağlı)</span>
                </label>
                <input
                  type="url"
                  value={uploadLink}
                  onChange={e => setUploadLink(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398]"
                />
                <p className="text-[#465886] text-xs mt-1">Ziyaretçi reklama tıklayınca bu linke yönlendirilir</p>
              </div>

              {uploadError && (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/25 rounded-xl px-3 py-2.5">
                  <i className="ri-error-warning-line text-red-400 text-sm" />
                  <p className="text-red-400 text-xs">{uploadError}</p>
                </div>
              )}

              <button
                onClick={handleUpload}
                disabled={uploading || !uploadFile_}
                className="w-full py-3 bg-[#3B82F6] hover:bg-[#3d4e6a] text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {uploading
                  ? <><i className="ri-loader-4-line animate-spin" /> Sunucuya Yükleniyor...</>
                  : <><i className="ri-upload-cloud-2-line" /> Reklamı Yükle &amp; Kaydet</>
                }
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
